import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';

import {
  Radio, Users, Play, Plus, Loader2, Video, Search,
  RefreshCw, Lock, Crown, Globe, Mic2, Gamepad2,
  Heart, Tv, ArrowUpDown, Eye, User
} from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: 'All', icon: Radio },
  { id: 'music', label: 'Music', icon: Mic2 },
  { id: 'gaming', label: 'Gaming', icon: Gamepad2 },
  { id: 'podcast', label: 'Podcast', icon: Mic2 },
  { id: 'lifestyle', label: 'Lifestyle', icon: Heart },
  { id: 'irl', label: 'IRL', icon: Tv },
];

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest' },
  { id: 'viewers', label: 'Most Viewers' },
  { id: 'trending', label: 'Trending' },
];

export default function LivePage() {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, user } = useAuth();

  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canGoLive, setCanGoLive] = useState(false);
  const [goLiveReason, setGoLiveReason] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newStream, setNewStream] = useState({
    title: '', description: '', category: 'music', sub_category: '',
    required_listener_tier: 'none'
  });
  const [tierRequirements, setTierRequirements] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('live');
  const [streamers, setStreamers] = useState([]);
  const [loadingStreamers, setLoadingStreamers] = useState(false);
  const [recentReplays, setRecentReplays] = useState([]);
  const [loadingReplays, setLoadingReplays] = useState(false);

  useEffect(() => {
    fetchStreams();
    fetchTierRequirements();
    fetchRecentReplays();
    if (isAuthenticated) checkGoLiveEligibility();
  }, [isAuthenticated]);

  useEffect(() => { fetchStreams(); }, [activeCategory, sortBy, searchQuery]);
  useEffect(() => { if (activeTab === 'streamers') fetchStreamers(); }, [activeTab]);

  const fetchTierRequirements = async () => {
    try {
      const r = await fetch(`${API}/live/tier-requirements`);
      if (r.ok) { const d = await r.json(); setTierRequirements(d.tiers || []); }
    } catch (e) { console.error(e); }
  };

  const fetchStreams = async () => {
    setLoading(true);
    try {
      let url = `${API}/live/streams?status=live&sort_by=${sortBy}`;
      if (activeCategory !== 'all') url += `&category=${activeCategory}`;
      if (searchQuery.trim()) url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      const r = await fetch(url);
      if (r.ok) { const d = await r.json(); setStreams(d.streams || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchStreamers = async () => {
    setLoadingStreamers(true);
    try {
      const r = await fetch(`${API}/live/discover-streamers`);
      if (r.ok) { const d = await r.json(); setStreamers(d.streamers || []); }
    } catch (e) { console.error(e); }
    setLoadingStreamers(false);
  };

  const fetchRecentReplays = async () => {
    setLoadingReplays(true);
    try {
      const r = await fetch(`${API}/live/replays?limit=8`);
      if (r.ok) { const d = await r.json(); setRecentReplays(d.replays || []); }
    } catch (e) { console.error(e); }
    setLoadingReplays(false);
  };

  const checkGoLiveEligibility = async () => {
    try {
      const r = await fetch(`${API}/live/can-go-live`, { headers: getAuthHeaders() });
      if (r.ok) { const d = await r.json(); setCanGoLive(d.can_go_live); setGoLiveReason(d.reason); }
    } catch (e) { console.error(e); }
  };

  const handleCreateStream = async () => {
    if (!newStream.title.trim()) { toast.error('Please enter a stream title'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/live/streams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          title: newStream.title,
          description: newStream.description,
          category: newStream.category,
          sub_category: newStream.sub_category || null,
          max_viewers: 1000,
          required_listener_tier: newStream.required_listener_tier !== 'none' ? newStream.required_listener_tier : null
        })
      });
      if (r.ok) {
        const d = await r.json();
        toast.success('Stream created!');
        navigate(`/go-live/${d.stream_id}`);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create stream');
      }
    } catch (e) { toast.error('Network error'); }
    setCreating(false);
  };

  const handleWatchStream = (streamId) => {
    if (!isAuthenticated) { toast.info('Log in to watch'); navigate('/login'); return; }
    navigate(`/watch/${streamId}`);
  };

  const formatDuration = (s) => {
    if (!s || isNaN(s)) return '';
    const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="live-page">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Radio className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">Live</h1>
                <p className="text-sm text-muted-foreground">Watch creators streaming now</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => { fetchStreams(); fetchRecentReplays(); }}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              {isAuthenticated && (
                <Button
                  onClick={() => canGoLive ? setShowCreateModal(true) : toast.info(goLiveReason)}
                  className={canGoLive ? 'bg-red-500 hover:bg-red-600' : ''}
                  variant={canGoLive ? 'default' : 'outline'}
                  data-testid="go-live-cta-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />Go Live
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Tabs: Live Now | Replays | Discover Streamers */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-muted/30 p-1">
            <TabsTrigger value="live" className="flex items-center gap-2" data-testid="live-tab-live">
              <Radio className="w-4 h-4" />Live Now
            </TabsTrigger>
            <TabsTrigger value="replays" className="flex items-center gap-2" data-testid="live-tab-replays">
              <Play className="w-4 h-4" />Replays
            </TabsTrigger>
            <TabsTrigger value="streamers" className="flex items-center gap-2" data-testid="live-tab-streamers">
              <Users className="w-4 h-4" />Discover Streamers
            </TabsTrigger>
          </TabsList>

          {/* LIVE NOW TAB */}
          <TabsContent value="live">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {CATEGORIES.map(cat => {
                  const CIcon = cat.icon;
                  return (
                    <Button key={cat.id} size="sm"
                      variant={activeCategory === cat.id ? 'default' : 'outline'}
                      className={activeCategory === cat.id ? 'bg-red-500 hover:bg-red-600' : 'border-border'}
                      onClick={() => setActiveCategory(cat.id)}
                      data-testid={`category-${cat.id}`}
                    >
                      <CIcon className="w-3 h-3 mr-1" />{cat.label}
                    </Button>
                  );
                })}
              </div>
              <div className="flex gap-2 ml-auto">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Search streams..." className="pl-9 w-48 h-9"
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    data-testid="search-streams-input" />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-36 h-9" data-testid="sort-streams">
                    <ArrowUpDown className="w-3 h-3 mr-1" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Stream Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : streams.length === 0 ? (
              <div className="text-center py-20">
                <Video className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h2 className="text-xl font-bold mb-2">No Live Streams</h2>
                <p className="text-muted-foreground mb-4">No one is streaming right now. Check back later or start your own!</p>
                {isAuthenticated && canGoLive && (
                  <Button onClick={() => setShowCreateModal(true)} className="bg-red-500 hover:bg-red-600">
                    <Radio className="w-4 h-4 mr-2" />Be the First to Go Live
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {streams.map(stream => (
                  <Card key={stream.stream_id}
                    className="glass-card border-border overflow-hidden cursor-pointer hover:border-red-500/40 transition-all group"
                    onClick={() => handleWatchStream(stream.stream_id)}
                    data-testid={`stream-card-${stream.stream_id}`}
                  >
                    <div className="aspect-video bg-gradient-to-br from-red-900/20 to-zinc-900 relative">
                      {stream.thumbnail_url ? (
                        <img src={stream.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="w-12 h-12 text-muted-foreground/50 group-hover:text-muted-foreground/80 transition-colors" />
                        </div>
                      )}
                      <Badge className="absolute top-2 left-2 bg-red-600 text-white">
                        <Radio className="w-3 h-3 mr-1 animate-pulse" />LIVE
                      </Badge>
                      {stream.category && (
                        <Badge className="absolute top-2 right-2 bg-background/60 text-foreground text-xs">{stream.category}</Badge>
                      )}
                      {stream.required_listener_tier && stream.required_listener_tier !== 'none' && (
                        <Badge className="absolute bottom-2 left-2 bg-amber-500/90 text-black text-xs">
                          <Lock className="w-3 h-3 mr-1" />{stream.tier_requirement_name || 'Members Only'}
                        </Badge>
                      )}
                      <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-background/60 rounded px-2 py-1 text-xs text-foreground">
                        <Users className="w-3 h-3" />{stream.viewer_count || 0}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold truncate text-sm">{stream.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {stream.host_avatar && (
                          <img src={stream.host_avatar} alt="" className="w-5 h-5 rounded-full" />
                        )}
                        <p className="text-sm text-muted-foreground truncate">{stream.host_name}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* REPLAYS TAB */}
          <TabsContent value="replays">
            {loadingReplays ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : recentReplays.length === 0 ? (
              <div className="text-center py-20">
                <Play className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h2 className="text-xl font-bold mb-2">No Replays Yet</h2>
                <p className="text-muted-foreground">Stream replays will appear here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {recentReplays.map(r => (
                  <Card key={r.replay_id}
                    className="glass-card border-border overflow-hidden cursor-pointer hover:border-red-500/30 transition-all group"
                    onClick={() => navigate(`/live/replay/${r.replay_id}`)}
                    data-testid={`replay-card-${r.replay_id}`}
                  >
                    <div className="aspect-video bg-card relative flex items-center justify-center">
                      {r.cover_image || r.thumbnail_url ? (
                        <img src={r.cover_image || r.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Play className="w-10 h-10 text-red-400/40 group-hover:text-red-400 transition-colors" />
                      )}
                      {r.duration_seconds && (
                        <Badge className="absolute bottom-2 right-2 bg-background/70 text-foreground text-xs">
                          {formatDuration(r.duration_seconds)}
                        </Badge>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-sm truncate">{r.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        {r.author_avatar && <img src={r.author_avatar} alt="" className="w-4 h-4 rounded-full" />}
                        <p className="text-xs text-muted-foreground truncate">{r.author_name}</p>
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" />{r.views || 0}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* DISCOVER STREAMERS TAB */}
          <TabsContent value="streamers">
            {loadingStreamers ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : streamers.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h2 className="text-xl font-bold mb-2">No Streamers Yet</h2>
                <p className="text-muted-foreground">Be the first to start streaming!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {streamers.map(s => (
                  <Card key={s.user_id}
                    className="glass-card border-border overflow-hidden cursor-pointer hover:border-red-500/30 transition-all"
                    onClick={() => navigate(`/u/${s.handle}`)}
                    data-testid={`streamer-card-${s.handle}`}
                  >
                    <div className="p-4 flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {s.avatar_url ? (
                          <img src={s.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-red-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-sm truncate">{s.name}</h4>
                          {s.is_live && (
                            <Badge className="bg-red-600 text-foreground text-xs px-1.5 py-0">
                              <Radio className="w-2 h-2 mr-0.5 animate-pulse" />LIVE
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{s.handle ? `@${s.handle}` : ""}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{s.category}</Badge>
                          <span className="text-xs text-muted-foreground">{s.stream_count} streams</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Stream Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-background border-border" data-testid="create-stream-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radio className="w-5 h-5 text-red-400" />Start a Live Stream
            </DialogTitle>
            <DialogDescription>Set up your stream details before going live.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Stream Title *</label>
              <Input placeholder="What are you streaming today?" value={newStream.title}
                onChange={e => setNewStream(s => ({ ...s, title: e.target.value }))}
                data-testid="stream-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea placeholder="Tell viewers what to expect..." value={newStream.description}
                onChange={e => setNewStream(s => ({ ...s, description: e.target.value }))} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Select value={newStream.category} onValueChange={v => setNewStream(s => ({ ...s, category: v }))}>
                  <SelectTrigger data-testid="stream-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Sub-Category</label>
                <Input placeholder="e.g., Hip-Hop, FPS..." value={newStream.sub_category}
                  onChange={e => setNewStream(s => ({ ...s, sub_category: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                {newStream.required_listener_tier === 'none' ? <Globe className="w-4 h-4 text-green-400" /> : <Lock className="w-4 h-4 text-amber-400" />}
                Who Can Watch
              </Label>
              <Select value={newStream.required_listener_tier} onValueChange={v => setNewStream(s => ({ ...s, required_listener_tier: v }))}>
                <SelectTrigger data-testid="listener-tier-select"><SelectValue placeholder="Select access level" /></SelectTrigger>
                <SelectContent>
                  {tierRequirements.map(tier => (
                    <SelectItem key={tier.tier_id} value={tier.tier_id}>
                      <div className="flex items-center gap-2">
                        {tier.tier_id === 'none' ? <Globe className="w-4 h-4 text-green-400" /> : <Crown className="w-4 h-4 text-amber-400" />}
                        <span>{tier.name}</span>
                        {tier.min_coins > 0 && <span className="text-xs text-muted-foreground">({tier.min_coins.toLocaleString()}+ coins)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateStream} disabled={creating || !newStream.title.trim()}
              className="w-full bg-red-500 hover:bg-red-600" data-testid="create-stream-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Video className="w-4 h-4 mr-2" />Start Broadcast</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
